import { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import { CategoryBadge, formatDate } from "@/lib/categories";
import PostContent from "@/components/PostContent";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(undefined); // undefined = loading, null = not found
    const [related, setRelated] = useState([]);

    useEffect(() => {
        let alive = true;
        setPost(undefined);
        axios.get(`${API}/posts/${slug}`)
            .then(({ data }) => { if (alive) setPost(data); })
            .catch(() => { if (alive) setPost(null); });
        return () => { alive = false; };
    }, [slug]);

    useEffect(() => {
        if (!post) return;
        let alive = true;
        axios.get(`${API}/posts`).then(({ data }) => {
            if (!alive) return;
            const list = data
                .filter((p) => p.slug !== post.slug && p.category === post.category)
                .slice(0, 3);
            setRelated(list);
        }).catch(() => {});
        return () => { alive = false; };
    }, [post]);

    useSEO({
        title: post?.title || (post === null ? "Article Not Found" : "Loading"),
        description: post?.excerpt,
        path: `/blog/${slug}`,
        schema: post ? {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.date,
            image: post.image,
            author: { "@type": "Organization", name: "Chesterton" },
            publisher: {
                "@type": "Organization",
                name: "Chesterton",
                url: typeof window !== "undefined" ? window.location.origin : "https://chesterton.law"
            }
        } : null
    });

    if (post === null) return <Navigate to="/blog" replace />;
    if (post === undefined) {
        return <div className="min-h-[60vh] flex items-center justify-center text-acre-muted text-sm" data-testid="post-loading">Loading…</div>;
    }

    return (
        <>
            <article data-testid={`blog-post-${post.slug}`}>
                <section className="bg-acre-bg">
                    <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-16 pt-12 lg:pt-16 pb-10">
                        <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-acre-muted hover:text-acre-primary mb-8 transition-colors">
                            <ArrowLeft size={14} /> All articles
                        </Link>
                        <CategoryBadge category={post.category} />
                        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-acre-secondary mt-6">
                            {post.title}
                        </h1>
                        <p className="mt-6 text-acre-muted text-lg lg:text-xl leading-relaxed">{post.excerpt}</p>
                        <div className="mt-8 flex items-center gap-6 text-xs text-acre-muted border-b border-acre-line pb-8">
                            <span className="flex items-center gap-2"><Calendar size={13} /> {formatDate(post.date)}</span>
                            <span className="flex items-center gap-2"><Clock size={13} /> {post.read_time}</span>
                            <span className="flex items-center gap-2">By <span className="text-acre-ink font-medium">Chesterton</span></span>
                        </div>
                    </div>
                </section>

                <section className="bg-acre-bg">
                    <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-16">
                        <div className="relative aspect-[16/9] overflow-hidden bg-acre-line">
                            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                        </div>
                    </div>
                </section>

                <section className="py-16 lg:py-24 bg-acre-bg">
                    <div className="max-w-[760px] mx-auto px-6 md:px-10 lg:px-16">
                        <PostContent html={post.content_html} />

                        <div className="mt-16 pt-10 border-t border-acre-line">
                            <div className="text-[11px] uppercase tracking-[0.25em] text-acre-secondary mb-3">Disclaimer</div>
                            <p className="text-xs text-acre-muted leading-relaxed">
                                This article is provided for general informational purposes only and does not constitute legal advice or create an attorney–client relationship. Laws differ by state and change over time; consult counsel licensed in your jurisdiction for guidance on your specific situation.
                            </p>
                        </div>
                    </div>
                </section>

                {related.length > 0 && (
                    <section className="py-20 lg:py-24 bg-white border-y border-acre-line" data-testid="related-posts">
                        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                            <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.28em] text-acre-secondary mb-3">Keep reading</div>
                                    <h2 className="font-serif-display text-3xl lg:text-5xl text-acre-secondary leading-tight">More on this topic.</h2>
                                </div>
                                <Link to="/blog" data-testid="all-articles-link" className="inline-flex items-center gap-2 text-sm text-acre-primary hover:gap-3 transition-all">
                                    All articles <ArrowRight size={16} />
                                </Link>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                                {related.map((r) => (
                                    <Link
                                        key={r.slug}
                                        to={`/blog/${r.slug}`}
                                        data-testid={`related-${r.slug}`}
                                        className="group bg-acre-bg border border-acre-line overflow-hidden flex flex-col hover:-translate-y-1 transition-all"
                                    >
                                        <div className="relative aspect-[4/3] overflow-hidden bg-acre-line">
                                            <img src={r.image} alt={r.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        </div>
                                        <div className="p-6">
                                            <CategoryBadge category={r.category} />
                                            <h3 className="font-serif-display text-xl text-acre-ink mt-3 leading-snug group-hover:text-acre-secondary transition-colors">
                                                {r.title}
                                            </h3>
                                            <div className="mt-4 text-xs text-acre-muted">{formatDate(r.date)} · {r.read_time}</div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                <CTABanner />
            </article>
        </>
    );
}
